import { useState, useEffect, useCallback } from "react";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import Header from "@/components/Header";
import TeamStatsOverview from "@/components/TeamStatsOverview";
import TopPerformers from "@/components/TopPerformers";
import LastGameRecap from "@/components/LastGameRecap";
import LiveGameTracker from "@/components/LiveGameTracker";
import TeamStatsDashboard from "@/components/TeamStatsDashboard";
import CompareTab from "@/components/CompareTab";
import TrendsTab from "@/components/TrendsTab";
import RankingsTab from "@/components/RankingsTab";
import YearOverYearTab from "@/components/YearOverYearTab";
import FieldingTab from "@/components/FieldingTab";
import PitcherOutingsTab from "@/components/PitcherOutingsTab";
import CareerVsTeamTab from "@/components/CareerVsTeamTab";
import TransactionsTab from "@/components/TransactionsTab";
import PlayerDetailPage from "@/components/PlayerDetailPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Dashboard() {
  const [teamStats, setTeamStats] = useState(null);
  const [topPerformers, setTopPerformers] = useState(null);
  const [hitters, setHitters] = useState([]);
  const [pitchers, setPitchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("home");

  const fetchTeamStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/team/stats`);
      setTeamStats(response.data);
    } catch (e) {
      console.error("Error fetching team stats:", e);
    }
  }, []);

  const fetchTopPerformers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/players/top-performers`);
      setTopPerformers(response.data);
    } catch (e) {
      console.error("Error fetching top performers:", e);
    }
  }, []);

  const fetchPlayers = useCallback(async () => {
    try {
      const [hittersRes, pitchersRes] = await Promise.all([
        axios.get(`${API}/players/hitters`),
        axios.get(`${API}/players/pitchers`)
      ]);
      setHitters(hittersRes.data.hitters || []);
      setPitchers(pitchersRes.data.pitchers || []);
    } catch (e) {
      console.error("Error fetching players:", e);
    }
  }, []);

  useEffect(() => {
    const loadInitialData = async () => {
      await fetchTeamStats();
      setLoading(false);
      fetchTopPerformers();
      fetchPlayers();
    };
    loadInitialData();
  }, [fetchTeamStats, fetchTopPerformers, fetchPlayers]);

  // Periodic refresh
  useEffect(() => {
    const interval = setInterval(() => {
      fetchTeamStats();
      fetchTopPerformers();
    }, 60000);
    return () => clearInterval(interval);
  }, [fetchTeamStats, fetchTopPerformers]);

  if (loading) {
    return (
      <div className="min-h-screen bg-astros-dark flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <div className="w-12 h-12 border-3 border-astros-orange border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="font-bebas text-xl text-astros-muted tracking-wider">Loading Astros Stats...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-astros-dark" data-testid="astros-dashboard">
      <Header />

      <main className="max-w-7xl mx-auto px-4 pb-16 pt-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full flex justify-start bg-transparent border-b border-astros-border overflow-x-auto">
            <TabsTrigger value="home" className="px-4 py-3 rounded-none border-b-2 border-transparent data-[state=active]:border-astros-orange data-[state=active]:bg-transparent data-[state=active]:text-astros-orange font-bebas text-lg tracking-wide" data-testid="tab-home">
              HOME
            </TabsTrigger>
            <TabsTrigger value="team-stats" className="px-4 py-3 rounded-none border-b-2 border-transparent data-[state=active]:border-astros-orange data-[state=active]:bg-transparent data-[state=active]:text-astros-orange font-bebas text-lg tracking-wide" data-testid="tab-team-stats">
              TEAM STATS
            </TabsTrigger>
            <TabsTrigger value="compare" className="px-4 py-3 rounded-none border-b-2 border-transparent data-[state=active]:border-astros-orange data-[state=active]:bg-transparent data-[state=active]:text-astros-orange font-bebas text-lg tracking-wide" data-testid="tab-compare">
              COMPARE
            </TabsTrigger>
            <TabsTrigger value="trends" className="px-4 py-3 rounded-none border-b-2 border-transparent data-[state=active]:border-astros-orange data-[state=active]:bg-transparent data-[state=active]:text-astros-orange font-bebas text-lg tracking-wide" data-testid="tab-trends">
              TRENDS
            </TabsTrigger>
            <TabsTrigger value="rankings" className="px-4 py-3 rounded-none border-b-2 border-transparent data-[state=active]:border-astros-orange data-[state=active]:bg-transparent data-[state=active]:text-astros-orange font-bebas text-lg tracking-wide" data-testid="tab-rankings">
              RANKINGS
            </TabsTrigger>
            <TabsTrigger value="yoy" className="px-4 py-3 rounded-none border-b-2 border-transparent data-[state=active]:border-astros-orange data-[state=active]:bg-transparent data-[state=active]:text-astros-orange font-bebas text-lg tracking-wide" data-testid="tab-yoy">
              vs 2025
            </TabsTrigger>
            <TabsTrigger value="fielding" className="px-4 py-3 rounded-none border-b-2 border-transparent data-[state=active]:border-astros-orange data-[state=active]:bg-transparent data-[state=active]:text-astros-orange font-bebas text-lg tracking-wide" data-testid="tab-fielding">
              FIELDING
            </TabsTrigger>
            <TabsTrigger value="outings" className="px-4 py-3 rounded-none border-b-2 border-transparent data-[state=active]:border-astros-orange data-[state=active]:bg-transparent data-[state=active]:text-astros-orange font-bebas text-lg tracking-wide" data-testid="tab-outings">
              OUTINGS
            </TabsTrigger>
            <TabsTrigger value="career-vs" className="px-4 py-3 rounded-none border-b-2 border-transparent data-[state=active]:border-astros-orange data-[state=active]:bg-transparent data-[state=active]:text-astros-orange font-bebas text-lg tracking-wide" data-testid="tab-career-vs">
              CAREER VS
            </TabsTrigger>
            <TabsTrigger value="transactions" className="px-4 py-3 rounded-none border-b-2 border-transparent data-[state=active]:border-astros-orange data-[state=active]:bg-transparent data-[state=active]:text-astros-orange font-bebas text-lg tracking-wide" data-testid="tab-transactions">
              TRANSACTIONS
            </TabsTrigger>
          </TabsList>

          <TabsContent value="home" className="mt-6 space-y-8" data-testid="content-home">
            <TeamStatsOverview stats={teamStats} />
            <TopPerformers data={topPerformers} />
            <LiveGameTracker />
            <LastGameRecap />
          </TabsContent>

          <TabsContent value="team-stats" className="mt-6" data-testid="content-team-stats">
            <TeamStatsDashboard hitters={hitters} pitchers={pitchers} />
          </TabsContent>

          <TabsContent value="compare" className="mt-6" data-testid="content-compare">
            <CompareTab hitters={hitters} pitchers={pitchers} />
          </TabsContent>

          <TabsContent value="trends" className="mt-6" data-testid="content-trends">
            <TrendsTab />
          </TabsContent>

          <TabsContent value="rankings" className="mt-6" data-testid="content-rankings">
            <RankingsTab />
          </TabsContent>

          <TabsContent value="yoy" className="mt-6" data-testid="content-yoy">
            <YearOverYearTab />
          </TabsContent>

          <TabsContent value="fielding" className="mt-6" data-testid="content-fielding">
            <FieldingTab />
          </TabsContent>

          <TabsContent value="outings" className="mt-6" data-testid="content-outings">
            <PitcherOutingsTab />
          </TabsContent>

          <TabsContent value="career-vs" className="mt-6" data-testid="content-career-vs">
            <CareerVsTeamTab />
          </TabsContent>

          <TabsContent value="transactions" className="mt-6" data-testid="content-transactions">
            <TransactionsTab />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/player/:playerId" element={<PlayerDetailPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
